import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useChatState } from '@core/chat';

import { getExistedPrivateChannel } from '@core/chat/helpers/channel';
import { getCatchErrorMessage } from '@helpers/error';
import {
  useNotification,
  getErrorNotification,
} from '@components/Notification';

import ChannelService from '@core/chat/services/ChannelService';
import { IParticipant } from '@interfaces/participants';

import {
  GetChannelProps,
  IEventChannel,
  IPrivateChannel,
  IStreamChannel,
  IStreamChat,
} from '@core/chat/StreamChat/interfaces';
import { ChatTabs } from '@core/chat/interfaces';

export const useStreamChannels = () => {
  const getStreamChannels = useCallback(
    async ({
      streamClient,
      userId,
      eventId,
      channelId = '',
      roomId = '',
    }: GetChannelProps): Promise<IStreamChannel[] | undefined> => {
      let finalChannelId = `mytaverse-event-${eventId}`;

      if (channelId) {
        finalChannelId = `chat-${eventId}-${channelId}`;
      } else if (roomId) {
        finalChannelId = `mytaverse-room-${roomId}`;
      }

      try {
        const channels = await streamClient.queryChannels({
          type: 'messaging',
          id: finalChannelId,
          members: { $in: [userId] },
        });
        return channels;
      } catch (error) {
        throw Error(getCatchErrorMessage(error));
      }
    },
    [],
  );

  const getEventStreamChannel = async ({
    streamClient,
    userId,
    eventId,
  }: GetChannelProps): Promise<IStreamChannel | null> => {
    const channels = await getStreamChannels({
      streamClient,
      userId,
      eventId,
    });

    const eventStreamChannel = channels?.length === 1 ? channels[0] : null;

    return eventStreamChannel;
  };

  const getEventRoomStreamChannel = async ({
    streamClient,
    userId,
    eventId,
    roomId,
  }: GetChannelProps): Promise<IStreamChannel | null> => {
    const channels = await getStreamChannels({
      streamClient,
      userId,
      eventId,
      roomId,
    });

    const eventStreamChannel = channels?.length === 1 ? channels[0] : null;

    return eventStreamChannel;
  };

  const getMapEventChannel = async (
    streamClient: IStreamChat,
    eventChannel: IEventChannel,
    currentUserId: string,
  ): Promise<IPrivateChannel> => {
    const channels = await getStreamChannels({
      streamClient,
      userId: currentUserId,
      eventId: eventChannel.eventId,
      channelId: eventChannel.id,
    });
    const channel = channels?.[0] as IStreamChannel;

    return { ...eventChannel, channel };
  };

  const getMapEventChannels = async (
    streamClient: IStreamChat,
    eventChannels: IEventChannel[],
    currentUserId: string,
  ): Promise<IPrivateChannel[]> => {
    const mapEventChannels = await Promise.all(
      eventChannels.map(async (eventChannel) => {
        const mapEventChannel = await getMapEventChannel(
          streamClient,
          eventChannel,
          currentUserId,
        );

        return mapEventChannel;
      }),
    );

    return mapEventChannels;
  };

  return {
    getStreamChannels,
    getEventStreamChannel,
    getEventRoomStreamChannel,
    getMapEventChannel,
    getMapEventChannels,
  };
};

export const useRedirectToPrivateChat = () => {
  const { currentEvent, streamChatMemberId, participants } =
    useMytaverseEvent();
  const { t: translate } = useTranslation();
  const { user } = useMytaverse();
  const {
    setOpen: setOpenChat,
    streamClient,
    setActiveTab,
    privateChannels,
    addPrivateChannel,
    setActivePrivateChannel,
    setLoadingPrivateChannel,
  } = useChatState();
  const { getStreamChannels } = useStreamChannels();
  const { showNotification } = useNotification();

  const createEventChannel = useCallback(
    async (participantUserId: string): Promise<IEventChannel | undefined> => {
      try {
        // reverse find my participant data
        const myParticipantId = participants.find(
          (participant) =>
            participant.streamChatMemberId === streamChatMemberId,
        ) as IParticipant;
        const userIDs = [
          myParticipantId.participantId || streamChatMemberId,
          participantUserId,
        ];

        const eventChannel = await ChannelService.createEventChannel(
          currentEvent?.eventId || '',
          userIDs,
        );
        return eventChannel;
      } catch (error) {
        const otherParticipant = participants.find(
          (participant) => participant.participantId === participantUserId,
        ) as IParticipant;

        const message = otherParticipant?.name
          ? translate(
              'notifications.chat.creatingNewConversationFailedWithParticipantName',
              {
                otherParticipantName: otherParticipant.name,
              },
            )
          : translate(
              'notifications.chat.creatingNewConversationFailedDefault',
            );
        showNotification(
          getErrorNotification({
            message,
            closeManually: true,
          }),
        );
        setLoadingPrivateChannel(null);
        throw Error(getCatchErrorMessage(error));
      }
    },
    [currentEvent?.eventId, streamChatMemberId],
  );

  const redirectToPrivateChat = useCallback(
    async (participantUserId: string) => {
      const participant = participants.find(
        (participant) =>
          participant.streamChatMemberId === participantUserId ||
          participant.participantId === participantUserId,
      ) as IParticipant;
      setLoadingPrivateChannel(participantUserId);
      let privateChannel = getExistedPrivateChannel(
        privateChannels,
        participant.participantId,
      );

      if (!privateChannel) {
        const eventChannel = await createEventChannel(participantUserId);

        if (!eventChannel || !streamClient) return;

        const channels = await getStreamChannels({
          streamClient,
          userId: streamChatMemberId,
          eventId: eventChannel.eventId,
          channelId: eventChannel.id,
        });
        const channel = channels?.[0] as IStreamChannel;
        privateChannel = { ...eventChannel, channel };

        addPrivateChannel(privateChannel);
      }

      setLoadingPrivateChannel(null);
      setActiveTab(ChatTabs.Private);
      setActivePrivateChannel(privateChannel);
      setOpenChat(true);
    },
    [
      privateChannels,
      setActiveTab,
      setActivePrivateChannel,
      createEventChannel,
      streamClient,
      getStreamChannels,
      user?.id,
      streamChatMemberId,
      addPrivateChannel,
    ],
  );

  return redirectToPrivateChat;
};

export const useRetryConnectionToChannels = () => {
  const { getEventStreamChannel, getEventRoomStreamChannel } =
    useStreamChannels();
  const { setEventStreamChannel, setEventRoomStreamChannel } = useChatState();

  const reconnectToEventStreamChannel = async ({
    streamClient,
    userId,
    eventId,
  }: GetChannelProps): Promise<void> => {
    const eventStreamChannel = await getEventStreamChannel({
      streamClient,
      userId,
      eventId,
    });

    setEventStreamChannel(eventStreamChannel);
  };

  const reconnectToEventRoomStreamChannel = async ({
    streamClient,
    userId,
    eventId,
    roomId,
  }: GetChannelProps): Promise<void> => {
    const eventRoomStreamChannel = await getEventRoomStreamChannel({
      streamClient,
      userId,
      eventId,
      roomId,
    });

    setEventRoomStreamChannel(eventRoomStreamChannel);
  };

  return {
    reconnectToEventStreamChannel,
    reconnectToEventRoomStreamChannel,
  };
};
